import React, { useState } from "react";
import { Disclose } from "@optimizely/axiom";
import { ProductInstance } from "../../../domain/ProductInstance";
import styles from "./OpalRoleSuggestionInstanceEnablement.module.scss";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { OrganizationProduct } from "../../../domain/OrganizationProduct";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { useOpalBranding } from "../../../hooks/useOpalBranding/useOpalBranding";
import { Flags } from "../../../feature-flags/flags";
import { GenAIDisabledProduct } from "./GenAIDisabledProduct";
import { GenAIInstanceCheckbox } from "./GenAIInstanceCheckbox";

type InstanceTableProps = {
    disabled?: boolean;
    instances: ProductInstance[];
    onSelect: ({ id }: { id: string }) => void;
    selectedIds?: string[];
    products: OrganizationProduct[];
};
export const OpalRoleSuggestionInstanceEnablement = ({
    disabled = false,
    instances,
    onSelect,
    products
}: InstanceTableProps) => {
    const { enabled: enableInstanceGenAIFlag } = useFeatureFlag(Flags.ENABLE_OPAL_AUTO_PROVISIONING);
    const { opalName } = useOpalBranding();

    const [idForConfirmation, setIdForConfirmation] = useState("");
    // Group instances by productName
    const grouped = instances.reduce<Record<string, ProductInstance[]>>((acc, instance) => {
        if (!acc[instance.productName]) acc[instance.productName] = [];
        acc[instance.productName].push(instance);
        return acc;
    }, {});

    const productIdsWithGenAISupport = products.map((product) => product?.supportsGenAI && product?.id).filter(Boolean);

    // Sort product names alphabetically and sort instances within each group
    const sortedGroupedEntries = Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
            ([productName, productInstances]) =>
                [
                    productName,
                    productInstances.sort((a, b) => {
                        const nameA = a.nickname || a.instanceName;
                        const nameB = b.nickname || b.instanceName;
                        return nameA.localeCompare(nameB);
                    })
                ] as [string, ProductInstance[]]
        );

    const handleCancel = () => {
        setIdForConfirmation("");
    };

    const handleConfirmation = () => {
        onSelect({ id: idForConfirmation });
        setIdForConfirmation("");
    };

    const handleToggle = ({ enabled, id }: { enabled: boolean; id: string }) => {
        if (enabled) {
            setIdForConfirmation(id);
        } else {
            onSelect({ id });
        }
    };

    return (
        <>
            <div className={styles["container"]}>
                {sortedGroupedEntries.map(([productName, productInstances], index) => {
                    const productHasGenAISupport = productIdsWithGenAISupport.includes(
                        productInstances[0]?.productId || ""
                    );

                    if (enableInstanceGenAIFlag && !productHasGenAISupport) {
                        return <GenAIDisabledProduct key={productName} productName={productName} />;
                    }

                    return (
                        <Disclose key={index} title={<div className={styles["container__heading"]}>{productName}</div>}>
                            {productInstances.map((instance) => (
                                <GenAIInstanceCheckbox
                                    key={instance.instanceId}
                                    disabled={disabled}
                                    enableInstanceGenAIFlag={enableInstanceGenAIFlag}
                                    handleToggle={handleToggle}
                                    instance={instance}
                                />
                            ))}
                        </Disclose>
                    );
                })}
            </div>
            {!!idForConfirmation && (
                <ConfirmationDialog
                    onCancel={handleCancel}
                    onConfirmation={handleConfirmation}
                    title={`Disable ${opalName}`}
                    callToActionText="Confirm"
                >
                    <div key={1}>
                        Turning off {opalName} will disable AI-powered features for this instance, including {opalName}{" "}
                        role suggestions when granting access.
                    </div>
                </ConfirmationDialog>
            )}
        </>
    );
};

OpalRoleSuggestionInstanceEnablement.displayName = "OpalRoleSuggestionInstanceEnablement";
